import { API_BASE_URL } from './config';
import { getDemoPayload } from './demoResponses';
import { isDemoMode } from './demoMode';
import { isAppOnlyPayload, redirectToAppOnly } from './appOnly';
import i18n from './i18n';

const selectedLocationHeader = () => {
  const locationId = localStorage.getItem('selected_location_id');
  return locationId ? { 'X-TimeTab-Location-ID': locationId } : {};
};

// 403 app_only: diese Rolle hat keinen Web-Zugang. Session wegraeumen und auf den
// App-Hinweis schicken, statt den Aufrufer mit einem Fehler zu belasten.
async function bounceIfAppOnly(response) {
  if (response.status !== 403) return false;

  let payload = null;
  try {
    payload = await response.clone().json();
  } catch {
    return false;
  }

  if (!isAppOnlyPayload(payload)) return false;
  redirectToAppOnly();
  return true;
}

export async function apiGet(endpoint, logout) {
  try {
    const demoPayload = getDemoPayload({ method: 'get', url: endpoint });
    if (demoPayload !== null) return demoPayload;

    const token = localStorage.getItem('session_token'); // ✅ Replace AsyncStorage with localStorage
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        ...selectedLocationHeader(),
      },
    });

    // 🔴 If the token is invalid or expired, server returns 401 => logout
    if (response.status === 401) {
      logout(); // Force logout
      return null;
    }

    if (await bounceIfAppOnly(response)) return null;

    // ⚠️ Handle other HTTP errors like 500 or 404
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Request failed: ${response.status} - ${errorText}`);
    }

    // ✅ Return JSON response if everything is okay
    return await response.json();
  } catch (err) {
    console.error('apiGet error:', err);
    throw err;
  }
}

export async function apiPost(endpoint, body, logout) {
  try {
    if (isDemoMode()) {
      window.dispatchEvent(new Event('timetab:demo-write-blocked'));
      throw new Error(i18n.t('demo_mode.read_only'));
    }

    const token = localStorage.getItem('session_token'); // ✅ Replace AsyncStorage with localStorage
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        ...selectedLocationHeader(),
      },
      body: JSON.stringify(body),
    });

    // 🔴 Handle expired or invalid token
    if (response.status === 401) {
      logout(); // Force logout
      return null;
    }

    if (await bounceIfAppOnly(response)) return null;

    // ⚠️ Handle other HTTP errors like 500 or 404
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Request failed: ${response.status} - ${errorText}`);
    }

    // ✅ Return JSON response if everything is okay
    return await response.json();
  } catch (err) {
    console.error('apiPost error:', err);
    throw err;
  }
}

let maintenanceChecked = false;

export async function checkMaintenanceOnce() {
  if (maintenanceChecked) return;
  if (isDemoMode()) return;

  try {
    const token = localStorage.getItem('session_token');

    const response = await fetch(`${API_BASE_URL}/api/web/config/maintenance`, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        ...selectedLocationHeader(),
      },
    });

    const data = await response.json();

    if (data.enabled) {
      alert(data.message || 'TimeTab is currently under maintenance.');
      window.location.href = '/maintenance'; // Or navigate programmatically in React
    }

    maintenanceChecked = true;
  } catch (err) {
    console.error('Maintenance check failed:', err);
    // Optional: allow normal usage if check fails
  }
}
